import { forwardRef } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Loader2 } from "lucide-react";

// 基本按鈕。圖示一律由呼叫端傳 lucide-react 元件,不用 emoji。

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** 左側圖示,傳入已渲染的 lucide 圖示節點。 */
  icon?: ReactNode;
  /** 執行中:顯示轉圈並停用,避免重複送出。 */
  loading?: boolean;
  /** 只有圖示時必填,否則螢幕閱讀器讀不到用途。 */
  "aria-label"?: string;
}

/** 按鈕的樣子。**連結要長得跟按鈕一樣的時候拿這一支** ——
 *  照抄一份 class 的下場是改了按鈕、那幾個連結沒跟著改。 */
export function buttonClass(
  variant: ButtonVariant = "secondary",
  size: ButtonSize = "md",
  extra = "",
): string {
  return `${BASE} ${VARIANTS[variant]} ${SIZES[size]} ${extra}`.trim();
}

const BASE =
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--radius)] " +
  "border font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-55";

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    "border-[var(--accent)] bg-[var(--accent)] text-[var(--on-accent)] hover:bg-[var(--accent-hover)] hover:border-[var(--accent-hover)]",
  secondary:
    "border-[var(--border-strong)] bg-[var(--surface)] text-[var(--text)] hover:bg-[var(--surface-2)]",
  ghost: "border-transparent bg-transparent text-[var(--text-2)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]",
  danger: "border-[var(--danger)] bg-[var(--danger)] text-[var(--on-danger)] hover:opacity-90",
};

const SIZES: Record<ButtonSize, string> = {
  sm: "h-8 px-2.5 text-sm",
  md: "h-9 px-3.5 text-sm",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", size = "md", icon, loading = false, disabled, className = "", children, ...rest },
  ref,
) {
  return (
    <button
      {...rest}
      ref={ref}
      type={rest.type ?? "button"}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={`${BASE} ${VARIANTS[variant]} ${SIZES[size]} ${className}`}
    >
      {loading ? <Loader2 aria-hidden="true" size={16} className="animate-spin" /> : icon}
      {children}
    </button>
  );
});
